import { useState } from "react";
import { X } from "lucide-react";
import { base44 } from '@/api/base44Client';
import AddressAutocomplete from './AddressAutocomplete';
import { useToast } from "@/components/ui/use-toast";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactModal({ isOpen, onClose }) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    message: ""
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Vennligst oppgi navn.";
    if (!formData.email.trim()) {
      next.email = "Vennligst oppgi e-post.";
    } else if (!EMAIL_RE.test(formData.email.trim())) {
      next.email = "Ugyldig e-postadresse.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate()) return;

    setLoading(true);
    try {
      await base44.functions.invoke('submitContactForm', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        message: formData.message.trim(),
      });
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", address: "", message: "" });
      setErrors({});
      setTimeout(() => {
        setSubmitted(false);
        onClose();
      }, 10000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Noe gikk galt",
        description: "Prøv igjen eller kontakt oss på info@lykke.info.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg max-w-md w-full text-center p-8">
          <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-4">Sendt</p>
          <h2 className="font-heading text-2xl font-light mb-4">Takk for meldingen!</h2>
          <p className="font-body text-2xl text-foreground/60">Vi kontakter deg snart.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-sm max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-heading text-2xl font-light">Kontakt</h2>
          <button
            onClick={onClose}
            aria-label="Lukk"
            className="p-1 hover:bg-secondary/30 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
          <div>
            <label className="block text-2xl font-body uppercase tracking-widest mb-2">
              Navn
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              aria-invalid={!!errors.name}
              className="w-full px-3 py-2 border border-border bg-background rounded text-foreground focus:outline-none focus:border-foreground"
              placeholder="Ditt navn"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-2xl font-body uppercase tracking-widest mb-2">
              E-post
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              aria-invalid={!!errors.email}
              className="w-full px-3 py-2 border border-border bg-background rounded text-foreground focus:outline-none focus:border-foreground"
              placeholder="din@email.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-2xl font-body uppercase tracking-widest mb-2">
              Telefon
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border bg-background rounded text-foreground focus:outline-none focus:border-foreground"
              placeholder="+47 XXXX XXXX"
            />
          </div>

          <div>
            <label className="block text-2xl font-body uppercase tracking-widest mb-2">
              Adresse
            </label>
            <AddressAutocomplete
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Din adresse..."
              className="w-full px-3 py-2 border border-border bg-background rounded text-foreground focus:outline-none focus:border-foreground"
            />
          </div>

          <div>
            <label className="block text-2xl font-body uppercase tracking-widest mb-2">
              Melding
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-border bg-background rounded text-foreground focus:outline-none focus:border-foreground resize-none"
              placeholder="Din melding..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border text-foreground hover:bg-secondary/30 rounded transition-colors text-2xl font-body uppercase tracking-widest"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed rounded transition-colors text-2xl font-body uppercase tracking-widest"
            >
              {loading ? 'Sender...' : 'Send'}
            </button>
          </div>
          <p className="text-xs text-foreground/60 font-body">
            Vi svarer vanligvis innen 24 timer.
          </p>
        </form>
      </div>
    </div>
  );
}